import { TabOption } from './types';

export const moveItemInArray = <T>(
  options: T[],
  indexFrom: number,
  indexTo: number
) =>
  options.reduce<T[]>((acc, option, index) => {
    if (indexFrom === indexTo) {
      return [...acc, option];
    } else if (index === indexFrom) {
      return acc;
    } else if (index === indexTo && indexTo > indexFrom) {
      return [...acc, option, options[indexFrom]];
    } else if (index === indexTo && indexTo < indexFrom) {
      return [...acc, options[indexFrom], option];
    }
    return [...acc, option];
  }, []);

export const getWidthUntilActiveTab = (
  indexOfActiveTab: number,
  tabs: TabOption[]
) =>
  tabs.reduce(
    (sumWidth, { width }, index) =>
      index > indexOfActiveTab ? sumWidth : sumWidth + width,
    0
  );

export const calculateTabsOrder = <T extends TabOption>(
  activeTabId: string | undefined,
  tabs: T[],
  wrapperWidth: number
): { sortedTabs: T[]; indexOfLastTabFittingToWrapper: number } => {
  const indexOfActiveTab = tabs.findIndex(({ tabId }) => tabId === activeTabId);
  const widthUntilActiveTab = getWidthUntilActiveTab(indexOfActiveTab, tabs);
  const widthOfAllComibnedTabs = tabs.reduce((sum, tab) => sum + tab.width, 0);
  const activeTabWidth = tabs[indexOfActiveTab]?.width;

  if (widthOfAllComibnedTabs <= wrapperWidth) {
    return {
      sortedTabs: tabs,
      indexOfLastTabFittingToWrapper: tabs.length - 1,
    };
  }

  // the wrapper width is smaller than the active tab
  if (activeTabWidth && wrapperWidth < activeTabWidth) {
    return {
      sortedTabs: tabs,
      indexOfLastTabFittingToWrapper: -1,
    };
  }

  // tabs are just fitting, no need for any shuffle
  if (wrapperWidth >= widthUntilActiveTab) {
    const indexOfLastTabFittingToWrapper =
      // find index of first overflowing tab and return one less
      tabs.findIndex(
        (_option, index) => getWidthUntilActiveTab(index, tabs) > wrapperWidth
      ) - 1;
    return {
      sortedTabs: tabs,
      indexOfLastTabFittingToWrapper,
    };
  }

  const tempTabs = moveItemInArray(tabs, indexOfActiveTab, 0);
  const indexOfLastTabFittingToWrapper =
    // find index of first overflowing tab and return one less
    tempTabs.findIndex(
      (_option, index) => getWidthUntilActiveTab(index, tempTabs) > wrapperWidth
    ) - 1;
  return {
    sortedTabs: moveItemInArray(
      tabs,
      indexOfActiveTab,
      indexOfLastTabFittingToWrapper
    ),
    indexOfLastTabFittingToWrapper,
  };
};
